<script setup>
import {
  computed, inject, reactive, ref,
} from 'vue';
import { cloneDeep, isEmpty, uniqueId } from 'lodash';
import FechaNacimientoForm from './FechaNacimientoForm.vue';
import FechaNacimientoView from './FechaNacimientoView.vue';
import EstructuraEscolarView from './EstructuraEscolarView.vue';
import EstructuraEscolarForm from './EstructuraEscolarForm.vue';
import ModalSimple from '../ModalSimple.vue';

const I18n = inject('I18n');

const DEF_CONDICIONES = {
  fecha_nacimiento: {
    tipo: 'fecha_nacimiento',
    label: I18n.t('fecha_nacimiento'),
    componente: FechaNacimientoView,
    formulario: FechaNacimientoForm,
    multiples: false,
    tamano: 'md',
  },
  curso_matriculado: {
    tipo: 'curso_matriculado',
    label: I18n.t('Matricula_curso'),
    componente: EstructuraEscolarView,
    formulario: EstructuraEscolarForm,
    multiples: false,
    tamano: 'lg',
  },
  estructura_escolar: {
    tipo: 'estructura_escolar',
    label: I18n.t('Estructura_escolar'),
    componente: EstructuraEscolarView,
    formulario: EstructuraEscolarForm,
    multiples: false,
    tamano: 'lg',
  },
};

const props = defineProps({
  condiciones: { type: Array, required: true },
  condicionesPermitidas: { type: Array, default: () => [] },
  // Irá en la clave el tipo de componente y en el hash de dentro, los atributos
  // que se le pasan al componente
  configuracionComponentes: { type: Object, default: () => ({}) },
  editable: { type: Boolean, default: true },
  titulo: { type: String, default: undefined },
  textoCondicionesVacias: { type: String, default: undefined },
});

const emits = defineEmits(['update:condiciones']);

const condicionesInternas = reactive(props.condiciones);
const condicionesJson = computed(() => (
  JSON.stringify(condicionesInternas)
));
const tituloFinal = computed(() => {
  // Si es null vamos a devolver null para que no muestre nada pero si es
  // undefined no se ha pasado el atributo y entonces usamos el valor por defecto
  if (props.titulo !== undefined) {
    return props.titulo;
  }
  return I18n.t('condiciones');
});
let definicionCondicionActual = null;
const condicionActual = ref();
const showModal = ref(false);

function puedeAnadirMasDeDefinicion(definicionCondicion) {
  if (definicionCondicion.multiples) {
    return true;
  }
  return !condicionesInternas.find((cond) => cond.tipo === definicionCondicion.tipo);
}

const condicionesValidasAnadir = computed(() => {
  const condicionesValidas = [];
  let permitidas = props.condicionesPermitidas;
  if (isEmpty(permitidas)) {
    permitidas = Object.keys(DEF_CONDICIONES);
  }
  permitidas.forEach((tipo) => {
    if (DEF_CONDICIONES[tipo]) {
      condicionesValidas.push(DEF_CONDICIONES[tipo]);
    }
  });
  return condicionesValidas;
});

const puedeAnadirMas = computed(() => {
  let puede = false;
  condicionesValidasAnadir.value.forEach((condicion) => {
    if (puedeAnadirMasDeDefinicion(condicion)) {
      puede = true;
    }
  });
  return puede;
});

function configuracionComponente(tipo) {
  return props.configuracionComponentes[tipo] || {};
}

function nuevaCondicion(definicionCondicion) {
  condicionActual.value = {
    tipo: definicionCondicion.tipo,
    // Cid es para buscar la que están editando y no tiene todavía id
    cid: uniqueId('condicion_'),
  };

  definicionCondicionActual = definicionCondicion;
  showModal.value = true;
}

function editarCondicion(index) {
  condicionesInternas[index].cid = uniqueId('condicion_');

  condicionActual.value = cloneDeep(condicionesInternas[index]);
  definicionCondicionActual = DEF_CONDICIONES[condicionActual.value.tipo];
  showModal.value = true;
}

function condicionGuardada(condicion) {
  const index = condicionesInternas.findIndex((c) => c.cid === condicion.cid);

  if (index === -1) {
    condicionesInternas.push(condicion);
  } else {
    condicionesInternas[index] = condicion;
  }
  showModal.value = false;

  emits('update:condiciones', condicionesInternas);
}

function eliminarCondicion(index) {
  if (index >= 0) {
    condicionesInternas.splice(index, 1);
  }

  emits('update:condiciones', condicionesInternas);
}
</script>

<template>
  <div>
    <div v-if="tituloFinal || editable" class="subtitulo-seccion">
      <div v-if="editable && puedeAnadirMas" class="dropdown pull-right">
        <button type="button" class="btn btn-default btn-xs" data-toggle="dropdown">
          <i class="fa fa-plus"></i> {{ $t('Anadir') }}
        </button>
        <ul class="dropdown-menu" aria-labelledby="dLabel">
          <li v-for="condicion in condicionesValidasAnadir" :key="condicion.label">
            <a
              :disabled="!puedeAnadirMasDeDefinicion(condicion) ? 'disabled' : ''"
              @click.prevent="nuevaCondicion(condicion)"
            >
              {{ $t(condicion.label) }}
            </a>
          </li>
        </ul>
      </div>
      {{ tituloFinal }}
    </div>
    <div v-if="!condicionesInternas?.length">
      {{ textoCondicionesVacias !== undefined ? textoCondicionesVacias : $t('condiciones_vacias') }}
    </div>
    <div
      v-for="(condicion, index) in condicionesInternas"
      :key="condicion"
      class="wrapper-componente m-b-sm"
    >
      <div>
        <component
          :is="DEF_CONDICIONES[condicion.tipo].componente"
          :condicion="condicionesInternas[index]"
          v-bind="configuracionComponente(condicion.tipo)"
        ></component>
      </div>
      <div v-if="editable">
        <button
          type="button"
          class="btn btn-xs btn-default m-r-xs"
          @click="editarCondicion(index)"
        >
          <i class="fa fa-fw fa-edit"></i>
        </button>
        <button
          v-confirmar
          type="button"
          class="btn btn-xs btn-danger"
          @confirmado="eliminarCondicion(index)"
        >
          <i class="fa fa-fw fa-times"></i>
        </button>
      </div>
    </div>
    <input type="hidden" name="condiciones_json" :value="condicionesJson" />

    <ModalSimple
      id="modal-form-condiciones"
      v-model="showModal"
      :titulo="definicionCondicionActual?.label"
      :tamano="definicionCondicionActual?.tamano"
      :autofocus="false"
    >
      <component
        :is="definicionCondicionActual.formulario"
        v-if="showModal"
        :condicion="condicionActual || {}"
        v-bind="configuracionComponente(definicionCondicionActual.tipo)"
        @aceptar="condicionGuardada($event)"
      ></component>
    </ModalSimple>
  </div>
</template>

<style lang="scss">
@import "@/../assets/stylesheets/cifra/vars/main.scss";

.wrapper-componente {
  display: flex;
  > :first-child {
    flex: 1;
  }
}
</style>
